class CartDrawer extends HTMLElement {
  /** @type {CartDrawer | undefined} */
  static instance;
  /** @type {boolean} */
  static upsellHandlersAttached = false;
  /** @type {Intl.NumberFormat | undefined} */
  static numberFormatter;

  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.setHeaderCartIconAccessibility();
    this.handleUpsellOptionChange = this.handleUpsellOptionChange.bind(this);
    this.handleUpsellAddClick = this.handleUpsellAddClick.bind(this);
  }

  open() {
    /** @type {HTMLDialogElement | null} */
    const dialog = /** @type {HTMLDialogElement | null} */ (document.querySelector('#cart-drawer'));
    if (dialog && typeof dialog.showModal === 'function') {
      dialog.showModal();
    }
  }

  close() {
    /** @type {HTMLDialogElement | null} */
    const dialog = /** @type {HTMLDialogElement | null} */ (document.querySelector('#cart-drawer'));
    if (!dialog) return;
    if (!dialog.open) return;
    if (typeof dialog.close === 'function') dialog.close();
  }

  connectedCallback() {
    CartDrawer.instance = this;

    if (!CartDrawer.upsellHandlersAttached) {
      document.addEventListener('change', this.handleUpsellOptionChange);
      document.addEventListener('click', this.handleUpsellAddClick);
      CartDrawer.upsellHandlersAttached = true;
    }

    this.initializeUpsellCards();
  }

  disconnectedCallback() {
    if (CartDrawer.instance !== this) return;

    document.removeEventListener('change', this.handleUpsellOptionChange);
    document.removeEventListener('click', this.handleUpsellAddClick);
    CartDrawer.upsellHandlersAttached = false;
    CartDrawer.instance = undefined;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    /** @type {HTMLDialogElement | null} */
    const dialog = /** @type {HTMLDialogElement | null} */ (document.querySelector('#cart-drawer'));
    
    if (!cartLink || !dialog) return;
    
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    
    // Open dialog on cart icon click
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      if (typeof dialog.showModal === 'function') dialog.showModal();
    });
    
    // Open dialog on space key or enter key
    cartLink.addEventListener('keydown', (event) => {
      /** @type {KeyboardEvent} */
      const e = /** @type {KeyboardEvent} */ (event);
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        if (typeof dialog.showModal === 'function') dialog.showModal();
      }
    });
    
    // Close dialog on outside click
    dialog.addEventListener('click', (event) => {
      // Check if the click target is the dialog element itself
      if (event.target === dialog) {
        this.close();
      }
    });
    
    dialog.addEventListener('cancel', () => {
      this.close();
    });

    // Close dialog on close button click (delegated so it survives re-renders)
    dialog.addEventListener('click', (event) => {
      const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
      if (target && target.closest('.drawer__close')) {
        event.preventDefault();
        this.close();
      }
    });

    // If URL has ?cart=open, auto-open the drawer, then clean the param
    try {
      const currentUrl = new URL(window.location.href);
      if (currentUrl.searchParams.get('cart') === 'open') {
        // Open after a tick to ensure dialog is in DOM
        setTimeout(() => {
          if (typeof dialog.showModal === 'function') dialog.showModal();
          // Clean up the query param without reloading
          currentUrl.searchParams.delete('cart');
          window.history.replaceState({}, '', currentUrl.toString());
        });
      }
    } catch (e) {
      // no-op
    }
  }

  /**
   * @param {*} parsedState
   */
  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];
      if (sectionElement && typeof sectionHtml === 'string') {
        sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);

        if (section.id === 'cart-drawer') {
          const upsellWrapper = this.getUpsellWrapper();
          if (upsellWrapper) {
            const upsellSource = this.getSectionDOM(sectionHtml, '.drawer__upsell');
            upsellWrapper.innerHTML = upsellSource ? upsellSource.innerHTML : '';
          }
        }
      }
    });

      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        this.classList.toggle('is-empty', cartDrawerItems.classList.contains('is-empty'));
      }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.initializeUpsellCards();
      const removeUpsellMeta = typeof window !== 'undefined'
        ? /** @type {(() => void) | undefined} */ ((/** @type {any} */ (window)).removeExtraUpsellMetaobjects)
        : undefined;
      if (typeof removeUpsellMeta === 'function') {
        try {
          removeUpsellMeta();
        } catch (error) {
          console.error(error);
        }
      }
      this.open();
    });
  }

  /**
   * @param {*} html
   * @param {string} [selector]
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  /**
   * @param {*} html
   * @param {string} [selector]
   */
  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  getUpsellWrapper() {
    const dialog = document.querySelector('#cart-drawer');
    return dialog ? dialog.querySelector('.drawer__upsell') : null;
  }

  getUpsellRoot() {
    return this.getUpsellWrapper()?.querySelector('.cart-drawer-upsell') || null;
  }

  /**
   * @returns {Intl.NumberFormat}
   */
  getCurrencyFormatter() {
    if (!CartDrawer.numberFormatter) {
      CartDrawer.numberFormatter = new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    }

    return CartDrawer.numberFormatter;
  }

  /**
   * @param {string} key
   * @param {string} fallback
   * @returns {string}
   */
  getRoute(key, fallback) {
    if (typeof window === 'undefined') return fallback;
    const globalWindow = /** @type {Record<string, unknown>} */ (/** @type {any} */ (window));
    const routesObj = /** @type {Record<string, string> | undefined} */ (globalWindow.routes);

    if (routesObj && typeof routesObj[key] === 'string' && routesObj[key]) {
      return routesObj[key];
    }

    return fallback;
  }

  /**
   * @param {string} body
   * @returns {RequestInit}
   */
  buildJsonRequest(body) {
    const baseConfig = (typeof fetchConfig === 'function' ? fetchConfig('json') : undefined) || {};
    const config = /** @type {RequestInit} */ (baseConfig);

    const headers = new Headers(config.headers || undefined);
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');
    config.headers = headers;

    if (!config.method) {
      config.method = 'POST';
    }

    config.body = body;

    return config;
  }

  initializeUpsellCards() {
    const upsellRoot = this.getUpsellRoot();
    if (!upsellRoot) return;

    const cards = upsellRoot.querySelectorAll('.upsell-card');
    cards.forEach((card) => this.updateUpsellCard(card));
  }

  /**
   * @param {Event} event
   */
  handleUpsellOptionChange(event) {
    const target = /** @type {EventTarget} */ (event.target);
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.classList.contains('upsell-option-select')) return;

    const upsellRoot = this.getUpsellRoot();
    if (!upsellRoot || !upsellRoot.contains(target)) return;

    const card = target.closest('.upsell-card');
    if (card) this.updateUpsellCard(card);
  }

  /**
   * @param {MouseEvent} event
   */
  handleUpsellAddClick(event) {
    const target = /** @type {EventTarget} */ (event.target);
    if (!(target instanceof Element)) return;

    const upsellRoot = this.getUpsellRoot();
    if (!upsellRoot) return;

    const button = target.closest('.cart-drawer-upsell .upsell-add-btn');
    if (!(button instanceof HTMLButtonElement)) return;
    if (!upsellRoot.contains(button)) return;

    const variantId = Number(button.getAttribute('data-variant-id'));
    if (!variantId) return;

    event.preventDefault();
    this.addUpsellVariant(variantId, button);
  }

  /**
   * @param {number} variantId
   * @param {HTMLButtonElement} button
   */
  addUpsellVariant(variantId, button) {
    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    const sections = this.getSectionsToRender().map((section) => section.id);
    const payload = {
      id: variantId,
      quantity: 1,
      sections,
      sections_url: window.location.pathname,
    };

    const requestConfig = this.buildJsonRequest(JSON.stringify(payload));

    const cartAddUrl = this.getRoute('cart_add_url', '/cart/add.js');

    fetch(cartAddUrl, requestConfig)
      .then((response) => response.json())
      .then((data) => {
        if (data?.status) {
          this.handleUpsellError(data.description);
          return;
        }

        if (data?.sections) {
          this.renderContents(data);
          this.dispatchUpsellCartUpdate(data, variantId);
          return;
        }

        return this.refreshCartSections(variantId);
      })
      .catch((error) => {
        console.error(error);
        return this.refreshCartSections(variantId);
      })
      .finally(() => {
        button.disabled = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      });
  }

  /**
   * @returns {Promise<Record<string, string> | null>}
   */
  requestCartSections() {
    const sectionsToRender = this.getSectionsToRender();
    if (!sectionsToRender.length) return Promise.resolve(null);

    const requests = sectionsToRender.map((section) => {
      const urlBase = this.getRoute('cart_url', '/cart');
      return fetch(`${urlBase}?section_id=${encodeURIComponent(section.id)}`)
        .then((response) => {
          if (!response.ok) throw new Error(`Failed to load section ${section.id}`);
          return response.text();
        });
    });

    return Promise.allSettled(requests).then((results) => {
      /** @type {Record<string, string>} */
      const sectionsMap = {};
      let hasSections = false;

      results.forEach((result, index) => {
        const section = sectionsToRender[index];
        if (!section) return;

        if (result.status === 'fulfilled') {
          sectionsMap[section.id] = result.value;
          hasSections = true;
        } else {
          console.error(result.reason);
        }
      });

      return hasSections ? sectionsMap : null;
    });
  }

  /**
   * @param {number} variantId
   * @returns {Promise<void>}
   */
  refreshCartSections(variantId) {
    return this.requestCartSections().then((sectionsMap) => {
      if (!sectionsMap) return;
      const fallbackState = { id: variantId, sections: sectionsMap };
      this.renderContents(fallbackState);
      this.dispatchUpsellCartUpdate(fallbackState, variantId);
    });
  }

  /**
   * @param {string | undefined} message
   */
  handleUpsellError(message) {
    if (message) {
      console.warn(message);
    }
  }

  /**
   * @param {Record<string, unknown>} parsedState
   * @param {number} variantId
   */
  dispatchUpsellCartUpdate(parsedState, variantId) {
    try {
      const globalWindow = typeof window !== 'undefined'
        ? /** @type {Record<string, unknown>} */ (/** @type {any} */ (window))
        : undefined;
      if (!globalWindow) return;

      const publishFn = /** @type {((eventName: string, payload: any) => void) | undefined} */ (globalWindow.publish);
      const pubSubEvents = /** @type {{ cartUpdate?: string } | undefined} */ (globalWindow.PUB_SUB_EVENTS);

      if (publishFn && pubSubEvents?.cartUpdate) {
        publishFn(pubSubEvents.cartUpdate, { source: 'cart-drawer-upsell', cartData: parsedState, variantId });
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @param {Element} card
   */
  updateUpsellCard(card) {
    const selects = card.querySelectorAll('.upsell-option-select');
    /** @type {(string | null)[]} */
    const options = [null, null, null];

    selects.forEach((selectElement) => {
      const select = selectElement instanceof HTMLSelectElement ? selectElement : null;
      if (!select) return;
      const index = Number(select.getAttribute('data-option-index')) - 1;
      if (index >= 0 && index < options.length) {
        options[index] = select.value;
      }
    });

    const variants = this.parseUpsellVariants(card);
    if (!variants.length) return;

    const variant = this.findUpsellVariant(variants, options) || variants[0];
    const priceEl = card.querySelector('[data-price]');
    const compareEl = card.querySelector('[data-compare-at]');
    const button = card.querySelector('.upsell-add-btn');

    if (!variant) {
      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      }
      return;
    }

    if (priceEl instanceof HTMLElement) priceEl.textContent = this.formatUpsellMoney(variant.price);

    const hasValidCompare = Boolean(variant.compare_at_price && variant.compare_at_price > variant.price);
    const comparePrice = hasValidCompare ? variant.compare_at_price : 0;

    if (compareEl instanceof HTMLElement) {
      if (comparePrice) {
        compareEl.style.display = '';
        compareEl.textContent = this.formatUpsellMoney(comparePrice);
      } else {
        compareEl.style.display = 'none';
        compareEl.textContent = '';
      }
    }

    if (button instanceof HTMLButtonElement) {
      button.disabled = variant.available === false;
      button.setAttribute('aria-disabled', variant.available === false ? 'true' : 'false');
      button.setAttribute('data-variant-id', String(variant.id));
      button.title = variant.available === false ? 'Sold out' : 'Add to cart';
    }

    this.updateUpsellImage(card, variant);
  }

  /**
   * @param {Element} card
   * @returns {Array<{ id: number; price: number; available?: boolean; compare_at_price?: number; option1?: string; option2?: string; option3?: string }>}
   */
  parseUpsellVariants(card) {
    const json = card.querySelector('.upsell-variants-json');
    if (!json) return [];

    try {
      return JSON.parse(json.textContent || '[]') || [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  /**
   * @param {Array<{ option1?: string; option2?: string; option3?: string }>} variants
   * @param {Array<string | null>} options
   * @returns {*}
   */
  findUpsellVariant(variants, options) {
    for (let index = 0; index < variants.length; index += 1) {
      const variant = variants[index];
      if (!variant) continue;
      const match = (
        (options[0] == null || variant.option1 === options[0]) &&
        (options[1] == null || variant.option2 === options[1]) &&
        (options[2] == null || variant.option3 === options[2])
      );

      if (match) return variant;
    }

    return null;
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  formatUpsellMoney(cents) {
    const amount = (Number(cents) || 0) / 100;

    try {
      const formatter = this.getCurrencyFormatter();
      let formatted = formatter.format(amount);
      formatted = formatted.replace(/\u00A0/g, ' ').trim();
      return `${formatted} €`;
    } catch (error) {
      console.error(error);
    }

    const rounded = Math.round(amount);
    const integerPart = String(rounded);
    const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const formatted = groupedInteger;
    return `${formatted} €`;
  }

  /**
   * @param {string} src
   * @param {number} width
   * @returns {string}
   */
  getResizedImageUrl(src, width) {
    if (!src) return '';

    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      const separator = src.includes('?') ? '&' : '?';
      return `${src}${separator}width=${width}`;
    }
  }

  /**
   * @param {Element} card
   * @param {{ featured_image?: { src?: string; url?: string; alt?: string; width?: number; height?: number }; image?: string }} variant
   */
  updateUpsellImage(card, variant) {
    const imageEl = card.querySelector('.upsell-image');
    if (!(imageEl instanceof HTMLImageElement)) return;

    const targetWidth = Number(imageEl.getAttribute('data-target-width')) || imageEl.width || 540;
    const defaultSrc = imageEl.getAttribute('data-default-src') || imageEl.src;
    const defaultAlt = imageEl.getAttribute('data-default-alt') || imageEl.alt;
    const defaultWidth = Number(imageEl.getAttribute('data-default-width')) || imageEl.width;
    const defaultHeight = Number(imageEl.getAttribute('data-default-height')) || imageEl.height;

    const featuredImage = variant?.featured_image;
    const variantSrc = featuredImage?.src || featuredImage?.url || variant?.image;

    if (variantSrc) {
      imageEl.src = this.getResizedImageUrl(variantSrc, targetWidth);
      if (featuredImage?.alt) {
        imageEl.alt = featuredImage.alt;
      }

      if (featuredImage?.width && featuredImage?.height) {
        imageEl.width = targetWidth;
        const scaledHeight = Math.round((featuredImage.height / featuredImage.width) * targetWidth);
        if (scaledHeight) {
          imageEl.height = scaledHeight;
        }
      } else {
        if (defaultWidth) imageEl.width = defaultWidth;
        if (defaultHeight) imageEl.height = defaultHeight;
      }

      return;
    }

    imageEl.src = defaultSrc;
    imageEl.alt = defaultAlt;
    if (defaultWidth) imageEl.width = defaultWidth;
    if (defaultHeight) imageEl.height = defaultHeight;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
